import React, { useRef, useState, useCallback } from 'react';
// Import Lucide icons
import { UploadCloud, FileCheck2, XCircle, Loader2, FileText } from 'lucide-react'; 
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import * as mammoth from 'mammoth';
// Removed unused Button and Alert imports
import { cn } from "@/lib/utils";
import { parsePdfText } from '@/lib/pdf/parser';

// Set worker source to local file path
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.js';

// Interface for extracted name information
interface NameInfo {
  fullName: string;
  firstName: string;
  lastName: string;
}

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onTextExtracted: (text: string) => void;
  onTitleExtracted?: (title: string) => void;
  onNameExtracted?: (nameInfo: NameInfo) => void; // New callback for extracted name
  accept?: string;
  label: string;
  type: 'cv' | 'job'; // "cv" or "job" to determine how to parse title
  maxSizeMB?: number; // Optional max file size in MB
}

export function FileUpload({
  onFileSelect,
  onTextExtracted,
  onTitleExtracted,
  onNameExtracted,
  accept = '.pdf,.docx,.txt,.rtf', // Added more formats
  label,
  type,
  maxSizeMB = 10, // Default 10MB max size
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState(0); // Added progress tracking
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Enhanced name extraction for CVs
  const extractNameFromCV = (text: string): NameInfo => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    
    // Common name patterns
    const patterns = [
      // Standard name pattern (First Last)
      /^([A-Z][a-z]+(?:-[A-Z][a-z]+)?)(?:\s+([A-Z][a-z]+(?:(?:\s+|-)[A-Z][a-z]+)?(?:\s+[A-Z][a-z]+)?))$/,
      
      // Name with middle initial (First M. Last)
      /^([A-Z][a-z]+)(?:\s+[A-Z]\.?)(?:\s+([A-Z][a-z]+(?:(?:\s+|-)[A-Z][a-z]+)?))$/,
      
      // Name with credentials (First Last, PhD)
      /^([A-Z][a-z]+)(?:\s+([A-Z][a-z]+(?:(?:\s+|-)[A-Z][a-z]+)?))(?:\s*,\s*(?:PhD|MBA|MD|CPA|PE|JD|PMP|CISSP|CFA|EA))/i,
      
      // Name with prefix (Dr. First Last)
      /^(?:Dr\.|Mr\.|Mrs\.|Ms\.|Prof\.)\s+([A-Z][a-z]+)(?:\s+([A-Z][a-z]+(?:(?:\s+|-)[A-Z][a-z]+)?))$/i,
      
      // Additional international name patterns
      /^([A-Z][a-zñáéíóúüÁÉÍÓÚÜ]+)(?:\s+([A-Z][a-zñáéíóúüÁÉÍÓÚÜ]+(?:(?:\s+|-)[A-Z][a-zñáéíóúüÁÉÍÓÚÜ]+)?))$/,
      
      // Name in ALL CAPS
      /^([A-Z]+)(?:\s+([A-Z]+(?:(?:\s+|-)[A-Z]+)?))$/,
    ];
    
    // Function to extract name from a line using multiple patterns
    const extractFromLine = (line: string): NameInfo | null => {
      // First try without normalization
      for (const pattern of patterns) {
        const match = line.match(pattern);
        if (match) {
          const [_, firstNameMatch, lastNameMatch] = match;
          if (firstNameMatch && lastNameMatch) {
            return {
              fullName: line.split(',')[0].trim(), // Remove any credentials
              firstName: firstNameMatch,
              lastName: lastNameMatch
            };
          }
        }
      }
      
      // Try with normalized line (capitalize first letter of each word)
      const normalizedLine = line
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
        
      for (const pattern of patterns) {
        const match = normalizedLine.match(pattern);
        if (match) {
          const [_, firstNameMatch, lastNameMatch] = match;
          if (firstNameMatch && lastNameMatch) {
            return {
              fullName: normalizedLine.split(',')[0].trim(),
              firstName: firstNameMatch,
              lastName: lastNameMatch
            };
          }
        }
      }
      
      return null;
    };
    
    // First try standard CV header sections (first few lines)
    for (let i = 0; i < Math.min(8, lines.length); i++) {
      const nameInfo = extractFromLine(lines[i]);
      if (nameInfo) {
        return nameInfo;
      }
    }
    
    // Look for "Name:" or similar patterns
    const nameLabels = ["name:", "full name:", "candidate name:", "applicant:", "curriculum vitae:", "resume:", "cv:"];
    for (let i = 0; i < Math.min(15, lines.length); i++) {
      const line = lines[i].toLowerCase();
      for (const label of nameLabels) {
        if (line.includes(label)) {
          const namePart = lines[i].substring(line.indexOf(label) + label.length).trim();
          // Try to extract from the name part
          const nameInfo = extractFromLine(namePart);
          if (nameInfo) {
            return nameInfo;
          }
          
          // Simple split if we can't match a pattern
          const nameParts = namePart.split(' ');
          if (nameParts.length >= 2) {
            return {
              fullName: namePart,
              firstName: nameParts[0],
              lastName: nameParts[nameParts.length - 1]
            };
          }
          
          // If this line has a label but no valid name, check the next line
          if (i + 1 < lines.length) {
            const nextLine = lines[i + 1].trim();
            const nextLineInfo = extractFromLine(nextLine);
            if (nextLineInfo) {
              return nextLineInfo;
            }
          }
        }
      }
    }
    
    // Phone/email patterns to identify contact section
    const contactPatterns = [
      /email\s*:?\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/i,
      /phone\s*:?\s*(?:\+\d{1,3}\s?)?(?:\(\d{3}\)\s?|\d{3}[-\s.]?)\d{3}[-\s.]?\d{4}/i,
      /(?:\+\d{1,3}\s?)?(?:\(\d{3}\)\s?|\d{3}[-\s.]?)\d{3}[-\s.]?\d{4}/i,
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/i
    ];
    
    // Look for a name above contact information
    for (let i = 0; i < Math.min(20, lines.length); i++) {
      for (const pattern of contactPatterns) {
        if (pattern.test(lines[i]) && i > 0) {
          // Check if line above is a potential name
          const nameInfo = extractFromLine(lines[i-1]);
          if (nameInfo) {
            return nameInfo;
          }
        }
      }
    }
    
    // Enhanced fallback: check for lines with 2-3 words that might be names
    for (let i = 0; i < Math.min(10, lines.length); i++) {
      const words = lines[i].split(' ').filter(Boolean);
      if (words.length >= 2 && words.length <= 3) {
        // Check if words start with uppercase
        const allWordsStartUppercase = words.every(word => /^[A-Z]/.test(word));
        if (allWordsStartUppercase) {
          return {
            fullName: lines[i],
            firstName: words[0],
            lastName: words[words.length - 1]
          };
        }
      }
    }
    
    // Fallback: use first line if it's reasonable
    const firstLine = lines[0] || '';
    if (firstLine && firstLine.split(' ').length >= 2 && firstLine.split(' ').length <= 5) {
      const parts = firstLine.split(' ');
      return {
        fullName: firstLine,
        firstName: parts[0],
        lastName: parts[parts.length - 1]
      };
    }
    
    // Final fallback: return empty/partial data
    return {
      fullName: firstLine,
      firstName: firstLine.split(' ')[0] || '',
      lastName: firstLine.split(' ').slice(1).join(' ') || ''
    };
  };

  // Enhanced title extraction with more patterns
  const extractTitle = (text: string): string => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    
    if (type === 'cv') {
      // For CV, extract name and return full name
      const nameInfo = extractNameFromCV(text);
      return nameInfo.fullName;
    } else {
      // Enhanced job title extraction with more patterns
      const titlePatterns = [
        /job title[:|\s]+(.+)/i,
        /position[:|\s]+(.+)/i,
        /role[:|\s]+(.+)/i,
        /job description for[:|\s]+(.+)/i,
        /we are looking for[:|\s]+(?:a|an)?\s*(.+)/i,
        /job opening[:|\s]+(.+)/i,
        /vacancy[:|\s]+(.+)/i,
        /career opportunity[:|\s]+(.+)/i,
        /^(.+)\s+job\s+description$/i
      ];
      
      // Try each pattern
      for (const pattern of titlePatterns) {
        const matchLine = lines.find(line => pattern.test(line));
        if (matchLine) {
          const match = matchLine.match(pattern);
          if (match && match[1]) {
            return match[1].trim();
          }
        }
      }
      
      // Fallback to first line
      return lines[0] || '';
    }
  };

  // Enhanced PDF text extraction that combines multiple methods
  const extractPdfText = async (file: File, arrayBuffer: ArrayBuffer): Promise<string> => {
    try {
      // First try client-side extraction
      const clientSideText = await extractPdfTextClientSide(arrayBuffer);
      
      // If we got a reasonable amount of text, use it
      if (clientSideText.length > 200) {
        return clientSideText;
      }
      
      // If client-side extraction didn't give us enough text, try using the server API
      try {
        const serverText = await parsePdfText(file);
        
        // If server extraction gave us text, use it
        if (serverText && serverText.length > 100) {
          return serverText;
        }
      } catch (serverErr) {
        console.warn("Server PDF extraction failed, falling back to client-side extraction:", serverErr);
      }
      
      // If we got here, return whatever we could extract from client-side
      return clientSideText;
    } catch (err) {
      console.error("PDF extraction error:", err);
      throw new Error("Failed to extract text from PDF. The file might be corrupted or password-protected.");
    }
  };
  
  // Client-side PDF extraction with enhanced layout handling
  const extractPdfTextClientSide = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf: PDFDocumentProxy = await loadingTask.promise;
    let fullText = '';
    
    const totalPages = pdf.numPages;
    
    for (let i = 1; i <= totalPages; i++) {
      try {
        // Update progress
        setProgress(Math.floor((i / totalPages) * 100));
        
        const page = await pdf.getPage(i);
        
        // Try structured text extraction first
        let pageText = await extractStructuredTextFromPage(page);
        
        // If structured extraction gave us very little text, try basic extraction
        if (pageText.length < 50) {
          const textContent = await page.getTextContent();
          pageText = textContent.items
            // @ts-ignore - We know str property exists
            .map(item => item.str || '')
            .join(' ');
        }
        
        fullText += pageText + '\n\n'; // Double newline between pages
      } catch (pageErr) {
        console.error(`Error processing page ${i} of PDF:`, pageErr);
        // Continue processing other pages
      }
    }
    
    // Post-process text to clean up common PDF extraction issues
    return postProcessExtractedText(fullText);
  };
  
  // Enhanced structured text extraction that handles columns and layout better
  const extractStructuredTextFromPage = async (page: PDFPageProxy): Promise<string> => {
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1.0 });
    const pageWidth = viewport.width;
    
    // First pass: collect all text items with their coordinates
    // @ts-ignore - We know the structure from PDF.js
    const textItems = textContent.items.map(item => {
      // @ts-ignore - We know these properties exist
      const transform = item.transform;
      const x = transform[4]; // horizontal position
      const y = transform[5]; // vertical position
      // @ts-ignore - We know str property exists
      const text = item.str || '';
      // @ts-ignore - Some PDF.js versions include width
      const width = item.width || 0;
      
      return { text, x, y, width };
    });
    
    // Detect if the page has multiple columns
    const hasMultipleColumns = detectMultipleColumns(textItems, pageWidth);
    
    if (hasMultipleColumns) {
      return extractMultiColumnText(textItems, pageWidth);
    } else {
      return extractSingleColumnText(textItems);
    }
  };
  
  // Detect if a page has multiple columns
  const detectMultipleColumns = (textItems: any[], pageWidth: number): boolean => {
    // Create a histogram of x positions
    const xPositions = textItems.map(item => item.x);
    const histogram: Record<number, number> = {};
    
    // Round x positions to nearest 10 pixels
    xPositions.forEach(x => {
      const roundedX = Math.round(x / 10) * 10;
      histogram[roundedX] = (histogram[roundedX] || 0) + 1;
    });
    
    // Find peaks in the histogram that might represent column starts
    const peaks: number[] = [];
    Object.entries(histogram).forEach(([x, count]) => {
      if (count > textItems.length * 0.1) { // If more than 10% of items start at this x
        peaks.push(Number(x));
      }
    });
    
    // If we have 2 or more peaks separated by at least 20% of page width, it's likely multiple columns
    if (peaks.length >= 2) {
      peaks.sort((a, b) => a - b);
      for (let i = 1; i < peaks.length; i++) {
        if (peaks[i] - peaks[i-1] > pageWidth * 0.2) {
          return true;
        }
      }
    }
    
    return false;
  };
  
  // Extract text from a page with multiple columns
  const extractMultiColumnText = (textItems: any[], pageWidth: number): string => {
    // Group items by which column they belong to
    const columnThreshold = pageWidth * 0.4; // 40% of page width as threshold
    const leftColumnItems = textItems.filter(item => item.x < columnThreshold);
    const rightColumnItems = textItems.filter(item => item.x >= columnThreshold);
    
    // Sort items vertically within each column
    const sortByY = (a: any, b: any) => b.y - a.y;
    leftColumnItems.sort(sortByY);
    rightColumnItems.sort(sortByY);
    
    // Extract text from left column
    const leftColumnText = extractSingleColumnText(leftColumnItems);
    
    // Extract text from right column
    const rightColumnText = extractSingleColumnText(rightColumnItems);
    
    // Combine both columns
    return leftColumnText + '\n\n' + rightColumnText;
  };
  
  // Extract text from a single column
  const extractSingleColumnText = (textItems: any[]): string => {
    // Sort items by vertical position (top to bottom)
    textItems.sort((a, b) => b.y - a.y);
    
    let lastY: number | null = null;
    let lastX: number | null = null;
    let text = '';
    
    // Process sorted items
    for (const item of textItems) {
      // Detect new line
      if (lastY !== null && Math.abs(item.y - lastY) > 5) {
        text += '\n';
        lastX = null; // Reset x position for new line
      } 
      // Detect same line but need space
      else if (lastX !== null && item.x - lastX > 10 && text.length > 0 && !text.endsWith(' ')) {
        text += ' ';
      }
      
      text += item.text;
      lastY = item.y;
      lastX = item.x + (item.width || 0);
    }
    
    return text;
  };
  
  // Clean up common PDF extraction issues
  const postProcessExtractedText = (text: string): string => {
    return text
      // Replace multiple spaces with a single space
      .replace(/\s{2,}/g, ' ')
      // Remove leading/trailing whitespace from lines
      .split('\n')
      .map(line => line.trim())
      // Remove empty lines but keep paragraph breaks (two consecutive newlines)
      .filter((line, i, arr) => line || (i > 0 && arr[i-1]))
      .join('\n')
      // Replace characters that often appear incorrectly in PDFs
      .replace(/[\u2018\u2019]/g, "'") // Smart quotes to straight quotes
      .replace(/[\u201C\u201D]/g, '"') // Smart double quotes to straight double quotes
      .replace(/\u2022/g, '•') // Standard bullet
      .replace(/\u2013/g, '-') // En dash to hyphen
      .replace(/\u2014/g, '-'); // Em dash to hyphen
  };
  
  // Enhanced DOCX extraction
  const extractDocxText = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    try {
      const result = await mammoth.extractRawText({ arrayBuffer });
      if (!result.value || result.value.length < 50) {
        // Try alternative approach with html extraction and convert to text
        const htmlResult = await mammoth.convertToHtml({ arrayBuffer });
        if (htmlResult.value) {
          // Simple HTML to text conversion
          return htmlResult.value
            .replace(/<[^>]*>/g, ' ') // Replace HTML tags with spaces
            .replace(/\s{2,}/g, ' ')  // Replace multiple spaces with a single space
            .trim();
        }
      }
      return result.value.trim();
    } catch (error) {
      console.error("Error extracting DOCX text:", error);
      throw new Error("Failed to extract text from DOCX file. The file might be corrupted.");
    }
  };
  
  // Extract text from TXT files
  const extractTxtText = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    try {
      // Try with UTF-8 first
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(arrayBuffer).trim();
      
      // If the text has lots of replacement characters, try another encoding
      if ((text.match(/\uFFFD/g) || []).length > text.length * 0.1) {
        try {
          // Try with ISO-8859-1 (Latin-1)
          const latin1Decoder = new TextDecoder('iso-8859-1');
          return latin1Decoder.decode(arrayBuffer).trim();
        } catch {
          // Fall back to the UTF-8 result
          return text;
        }
      }
      
      return text;
    } catch (error) {
      console.error("Error extracting TXT text:", error);
      throw new Error("Failed to extract text from TXT file.");
    }
  };
  
  // Extract text from RTF files
  const extractRtfText = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    try {
      // Basic RTF to text conversion
      const text = new TextDecoder('utf-8').decode(arrayBuffer);
      
      // Strip RTF control sequences
      return text
        .replace(/\{\\rtf[^{}]*\}/g, '')
        .replace(/\\\w+\s?/g, '')
        .replace(/\{|\}/g, '')
        .replace(/\\[^\s\\]+/g, '')
        .replace(/\\'[0-9a-f]{2}/g, '') // Remove hex character codes
        .replace(/\\u-?[0-9]+\s?/g, '') // Remove Unicode character codes
        .trim();
    } catch (error) {
      console.error("Error extracting RTF text:", error);
      throw new Error("Failed to extract text from RTF file.");
    }
  };

  // This function reads the file and extracts the full text with enhanced capabilities
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Reset state
    setRetryCount(0);
    
    // Process the file
    await processFile(file);
  };
  
  // Separate file processing function to allow for retries
  const processFile = async (file: File) => {
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSizeMB) {
      setError(`File too large (${fileSizeInMB.toFixed(1)}MB). Maximum size is ${maxSizeMB}MB.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFileName(file.name);
    setProgress(0);

    // Let the parent know a file was selected
    onFileSelect(file);

    try {
      const arrayBuffer = await file.arrayBuffer();
      let extractedText = '';
      let extractedTitle = '';
      let nameInfo: NameInfo | null = null;

      // Determine file type and use appropriate extraction method
      const fileType = file.name.toLowerCase();
      
      if (file.type === 'application/pdf' || fileType.endsWith('.pdf')) {
        extractedText = await extractPdfText(file, arrayBuffer);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        fileType.endsWith('.docx')
      ) {
        extractedText = await extractDocxText(arrayBuffer);
      } else if (file.type === 'text/plain' || fileType.endsWith('.txt')) {
        extractedText = await extractTxtText(arrayBuffer);
      } else if (file.type === 'application/rtf' || fileType.endsWith('.rtf')) {
        extractedText = await extractRtfText(arrayBuffer);
      } else {
        throw new Error(`Unsupported file type: ${file.type || 'unknown'}`);
      }

      // Validate extracted text
      if (!extractedText || extractedText.length < 10) {
        // If retry count is below max, try again with server API
        if (retryCount < MAX_RETRIES && (file.type === 'application/pdf' || fileType.endsWith('.pdf'))) {
          setRetryCount(prev => prev + 1);
          
          try {
            // Use server API as fallback
            extractedText = await parsePdfText(file);
            
            if (!extractedText || extractedText.length < 10) {
              throw new Error('Could not extract meaningful text from the file after multiple attempts.');
            }
          } catch (serverErr) {
            console.error('Server API extraction failed:', serverErr);
            throw new Error('Could not extract meaningful text from the file. The file might be password-protected or contain only images.');
          }
        } else {
          throw new Error('Could not extract meaningful text from the file. The file might be corrupted or contain only images.');
        }
      }

      // Extract title
      extractedTitle = extractTitle(extractedText);
      
      // Extract name information for CVs
      if (type === 'cv') {
        nameInfo = extractNameFromCV(extractedText);
      }

      // Inform parent components
      onTextExtracted(extractedText);
      if (onTitleExtracted) {
        onTitleExtracted(extractedTitle);
      }
      if (type === 'cv' && onNameExtracted && nameInfo) {
        onNameExtracted(nameInfo);
      }

    } catch (err: any) {
      console.error('Error processing file:', err);
      setError(err.message || 'An unexpected error occurred while processing the file.');
      setFileName(null);
    } finally {
      setIsLoading(false);
      setProgress(0);
      
      // Clear the file input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Use useCallback for drag-and-drop handlers to avoid redefining on each render
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      if (fileInputRef.current) {
        fileInputRef.current.files = event.dataTransfer.files;
        const changeEvent = new Event('change', { bubbles: true });
        fileInputRef.current.dispatchEvent(changeEvent);
      }
      event.dataTransfer.clearData();
    }
  }, []);

  // Get icon based on file extension
  const getFileIcon = () => {
    if (!fileName) return <UploadCloud className="h-10 w-10 text-muted-foreground/70 mb-3" />;
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (extension === 'pdf') {
      return <FileCheck2 className="h-10 w-10 text-green-500 mb-3" />;
    } else if (extension === 'docx') {
      return <FileText className="h-10 w-10 text-blue-500 mb-3" />;
    } else if (extension === 'txt') {
      return <FileText className="h-10 w-10 text-gray-500 mb-3" />;
    } else if (extension === 'rtf') {
      return <FileText className="h-10 w-10 text-orange-500 mb-3" />;
    }
    
    return <FileCheck2 className="h-10 w-10 text-green-500 mb-3" />;
  };

  // Function for manual retry when extraction fails
  const handleRetry = useCallback(() => {
    if (fileName) {
      setError(null);
      setRetryCount(0);
      // Trigger file input click to allow selecting a new file
      fileInputRef.current?.click();
    }
  }, [fileName]);

  return (
    <div className="space-y-2">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        accept={accept}
        onChange={handleFileChange}
        disabled={isLoading}
      />
      {/* Drop zone / Click area with enhanced UI */}
      <div
        onClick={() => !isLoading && fileInputRef.current?.click()} 
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors duration-200",
          isLoading
            ? 'cursor-not-allowed border-muted bg-muted/50'
            : 'cursor-pointer border-muted-foreground/30 hover:border-primary/50 hover:bg-primary/5',
          error ? 'border-destructive/50 bg-destructive/5' : ''
        )}
      >
        {/* Conditional Icon based on state */}
        {isLoading ? (
          <Loader2 className="h-10 w-10 text-primary animate-spin mb-3" />
        ) : error ? (
          <XCircle className="h-10 w-10 text-destructive mb-3" />
        ) : fileName ? (
          getFileIcon()
        ) : (
          <UploadCloud className="h-10 w-10 text-muted-foreground/70 mb-3" />
        )}

        {/* Text Content */}
        <p className="mb-1 text-sm font-medium text-foreground">
          {isLoading ? 'Processing File...' : 
           error ? 'Upload Failed' : 
           fileName ? fileName : 
           label}
        </p>
        <p className="text-xs text-muted-foreground">
          {isLoading ? `Please wait... ${progress > 0 ? `${progress}%` : ''}` : 
           error ? (
            <span>
              {error} <button onClick={(e) => { e.stopPropagation(); handleRetry(); }} className="text-primary underline">Try again</button>
            </span>
           ) : 
           fileName ? 'Click or drag file to replace' : 
           `Drag & drop or click to upload (PDF, DOCX, TXT, RTF) - Max ${maxSizeMB}MB`}
        </p>
        
        {/* Progress bar for loading state */}
        {isLoading && (
          <div className="w-full mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300 ease-in-out"
              style={{ width: `${progress > 0 ? progress : 50}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
